import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, ShieldCheck, FileText, Receipt, Briefcase, ExternalLink, LogOut, Plus, UserRound, MessageSquare } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { StatusPill } from '@/components/rm'
import { phFormat, peso } from '@/lib/ph'
import { BrandMark } from '@/components/brand'

export const Route = createFileRoute('/portal')({
  validateSearch: (s: Record<string, unknown>) => ({ token: s.token ? String(s.token) : '' }),
  component: PortalPage,
})

const KEY = 'sp_portal_token'
const money = (n: unknown, _c?: unknown) => peso(n)
const date = (v: unknown) => (v ? phFormat(new Date(v as string), 'date', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')
const coName = (r: Record<string, unknown>) => (r.company as { name?: string } | null)?.name ?? ''

interface PortalData {
  email: string
  businesses: { companyId: string; name: string }[]
  contact: { phone: string; address: string; city: string } | null
  quotes: Record<string, unknown>[]
  invoices: Record<string, unknown>[]
  jobs: Record<string, unknown>[]
}

function PortalPage() {
  const { token: urlToken } = Route.useSearch()
  const [token, setToken] = useState<string>('')
  const [data, setData] = useState<PortalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [reqOpen, setReqOpen] = useState(false)
  const [req, setReq] = useState({ companyId: '', subject: '', details: '' })
  const [det, setDet] = useState({ phone: '', address: '', city: '' })
  const [actBusy, setActBusy] = useState('')
  const [threads, setThreads] = useState<{ companyId: string; name: string; messages: { sender: string; body: string; created_at: string }[] }[]>([])
  const [msgText, setMsgText] = useState<Record<string, string>>({})

  const loadThreads = async (t: string) => {
    const { data } = await supabase.functions.invoke('portal-api', { body: { token: t, action: 'messages' } })
    if (data && !(data as { error?: string }).error) setThreads((data as { threads: typeof threads }).threads || [])
  }
  const sendMessage = async (companyId: string) => {
    const text = (msgText[companyId] ?? '').trim()
    if (!text) return
    setActBusy('msg:' + companyId)
    const { data, error } = await supabase.functions.invoke('portal-api', { body: { token, action: 'send_message', companyId, message: text } })
    setActBusy('')
    if (error || !(data as { ok?: boolean })?.ok) return toast.error('Could not send message')
    setMsgText((m) => ({ ...m, [companyId]: '' })); loadThreads(token)
  }

  const applyData = (d: PortalData) => {
    setData(d)
    setDet({ phone: d.contact?.phone ?? '', address: d.contact?.address ?? '', city: d.contact?.city ?? '' })
    setReq((r) => ({ ...r, companyId: r.companyId || d.businesses[0]?.companyId || '' }))
  }
  const reload = async (t: string) => {
    const { data } = await supabase.functions.invoke('portal-api', { body: { token: t } })
    if (data && !(data as { error?: string }).error) applyData(data as PortalData)
  }

  useEffect(() => {
    const t = urlToken || localStorage.getItem(KEY) || ''
    if (urlToken) { localStorage.setItem(KEY, urlToken); window.history.replaceState({}, '', '/portal') }
    setToken(t)
    if (!t) { setLoading(false); return }
    supabase.functions.invoke('portal-api', { body: { token: t } }).then(({ data, error }) => {
      if (error || (data as { error?: string })?.error) { setExpired(true); localStorage.removeItem(KEY) }
      else { applyData(data as PortalData); loadThreads(t) }
      setLoading(false)
    })
  }, [urlToken])

  const submitRequest = async () => {
    if (!req.subject.trim()) return toast.error('Tell us what you need')
    setActBusy('req')
    const { data, error } = await supabase.functions.invoke('portal-api', { body: { token, action: 'request', companyId: req.companyId, subject: req.subject, details: req.details } })
    setActBusy('')
    if (error || !(data as { ok?: boolean })?.ok) return toast.error('Could not send your request')
    toast.success('Request sent — the business will be in touch'); setReqOpen(false); setReq((r) => ({ ...r, subject: '', details: '' })); reload(token)
  }
  const saveDetails = async () => {
    setActBusy('det')
    const { data, error } = await supabase.functions.invoke('portal-api', { body: { token, action: 'update_contact', ...det } })
    setActBusy('')
    if (error || !(data as { ok?: boolean })?.ok) return toast.error('Could not save')
    toast.success('Details updated')
  }

  const requestLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    await supabase.functions.invoke('portal-auth', { body: { email } })
    setBusy(false); setSent(true)
  }
  const signOut = () => { localStorage.removeItem(KEY); setToken(''); setData(null); setExpired(false); setSent(false) }

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>

  // Not signed in → email entry
  if (!token || expired || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <BrandMark className="mx-auto mb-3 h-11 w-11" />
            <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">Your account</h1>
            <p className="mt-1 text-[13.5px] text-token-secondary">{expired ? 'That link expired. Enter your email for a new one.' : 'View your quotes, jobs and invoices.'}</p>
          </div>
          {sent ? (
            <div className="rounded-[16px] border border-token-subtle bg-token-surface p-6 text-center">
              <p className="text-[14px] font-semibold text-token-primary">Check your email</p>
              <p className="mt-1 text-[13px] text-token-secondary">If <b>{email}</b> is on file, we’ve sent a secure link to access your account.</p>
            </div>
          ) : (
            <form onSubmit={requestLink} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-token-secondary">Email address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" placeholder="you@email.com" />
              </div>
              <button type="submit" disabled={busy} className="h-10 w-full rounded-[10px] bg-brand text-[14px] font-bold text-brand-fg disabled:opacity-60">{busy ? 'Sending…' : 'Email me a link'}</button>
            </form>
          )}
          <p className="mt-5 flex items-center justify-center gap-1.5 text-[12px] text-token-muted"><ShieldCheck className="h-3.5 w-3.5" /> Secured by MySerbisyo</p>
        </div>
      </div>
    )
  }

  // Signed in → dashboard
  const Section = ({ icon, title, children, empty }: { icon: React.ReactNode; title: string; children: React.ReactNode; empty: boolean }) => (
    <div className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface">
      <div className="flex items-center gap-2 border-b border-token-subtle px-5 py-3.5"><span className="text-token-secondary">{icon}</span><span className="font-display text-[15px] font-bold text-token-primary">{title}</span></div>
      {empty ? <p className="px-5 py-8 text-center text-[13px] text-token-muted">Nothing here yet.</p> : <div className="divide-y divide-token-subtle">{children}</div>}
    </div>
  )

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <BrandMark className="h-9 w-9 flex-none" />
            <div className="min-w-0"><p className="font-display text-[16px] font-bold text-token-primary">Your account</p><p className="truncate text-[12px] text-token-secondary">{data.email}</p></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setReqOpen(true)} className="inline-flex items-center gap-1.5 rounded-[10px] bg-brand px-3 py-2 text-[12.5px] font-bold text-brand-fg hover:brightness-110"><Plus className="h-4 w-4" /> Request a job</button>
            <button onClick={signOut} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-token-secondary hover:text-token-primary"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>

        <div className="space-y-5">
          <Section icon={<FileText className="h-4 w-4" />} title="Quotes" empty={data.quotes.length === 0}>
            {data.quotes.map((q) => (
              <a key={String(q.id)} href={`/q/${q.share_token}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-token-surface">
                <div className="min-w-0 flex-1"><p className="truncate text-[14px] font-semibold text-token-primary">{String(q.title || q.quote_number || 'Quote')}</p><p className="text-[12px] text-token-secondary">{coName(q)} · {date(q.created_at)}</p></div>
                <span className="text-[13.5px] font-bold tabular-nums text-token-primary">{money(q.total, q.currency)}</span>
                <StatusPill status={String(q.status)} /><ExternalLink className="h-4 w-4 text-token-muted" />
              </a>
            ))}
          </Section>

          <Section icon={<Receipt className="h-4 w-4" />} title="Invoices" empty={data.invoices.length === 0}>
            {data.invoices.map((inv) => {
              const bal = (Number(inv.total) || 0) - (Number(inv.amount_paid) || 0)
              return (
                <a key={String(inv.id)} href={`/i/${inv.share_token}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-token-surface">
                  <div className="min-w-0 flex-1"><p className="truncate text-[14px] font-semibold text-token-primary">{String(inv.title || inv.invoice_number || 'Invoice')}</p><p className="text-[12px] text-token-secondary">{coName(inv)} · {bal > 0 ? `${money(bal, inv.currency)} due` : 'Paid'}</p></div>
                  <span className="text-[13.5px] font-bold tabular-nums text-token-primary">{money(inv.total, inv.currency)}</span>
                  <StatusPill status={String(inv.status)} /><ExternalLink className="h-4 w-4 text-token-muted" />
                </a>
              )
            })}
          </Section>

          <Section icon={<Briefcase className="h-4 w-4" />} title="Job history" empty={data.jobs.length === 0}>
            {data.jobs.map((j) => (
              <div key={String(j.id)} className="flex items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1"><p className="truncate text-[14px] font-semibold text-token-primary">{String(j.name || 'Job')}</p><p className="text-[12px] text-token-secondary">{coName(j)} · {date(j.planned_start_date)}</p></div>
                <StatusPill status={String(j.status)} />
              </div>
            ))}
          </Section>

          {data.businesses.map((b) => {
            const th = threads.find((t) => t.companyId === b.companyId)
            return (
              <div key={b.companyId} className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface">
                <div className="flex items-center gap-2 border-b border-token-subtle px-5 py-3.5"><span className="text-token-secondary"><MessageSquare className="h-4 w-4" /></span><span className="font-display text-[15px] font-bold text-token-primary">Messages{data.businesses.length > 1 ? ` · ${b.name}` : ''}</span></div>
                <div className="max-h-72 space-y-2 overflow-auto p-4">
                  {(!th || th.messages.length === 0) ? <p className="py-6 text-center text-[13px] text-token-muted">No messages yet. Say hello 👋</p> :
                    th.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] whitespace-pre-wrap rounded-[12px] px-3 py-2 text-[13.5px] ${m.sender === 'customer' ? 'bg-brand text-brand-fg' : 'bg-canvas-subtle text-token-primary'}`}>{m.body}</div>
                      </div>
                    ))}
                </div>
                <div className="flex gap-2 border-t border-token-subtle p-3">
                  <input value={msgText[b.companyId] ?? ''} onChange={(e) => setMsgText({ ...msgText, [b.companyId]: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(b.companyId) }} placeholder="Type a message…" className="h-10 flex-1 rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" />
                  <button onClick={() => sendMessage(b.companyId)} disabled={actBusy === 'msg:' + b.companyId} className="rounded-[10px] bg-brand px-4 text-[13px] font-bold text-brand-fg disabled:opacity-60">Send</button>
                </div>
              </div>
            )
          })}

          <div className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface">
            <div className="flex items-center gap-2 border-b border-token-subtle px-5 py-3.5"><span className="text-token-secondary"><UserRound className="h-4 w-4" /></span><span className="font-display text-[15px] font-bold text-token-primary">My details</span></div>
            <div className="grid gap-3 p-5 sm:grid-cols-2">
              <label className="text-[13px]"><span className="mb-1 block font-semibold text-token-secondary">Phone</span><input value={det.phone} onChange={(e) => setDet({ ...det, phone: e.target.value })} className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" /></label>
              <label className="text-[13px]"><span className="mb-1 block font-semibold text-token-secondary">Suburb / city</span><input value={det.city} onChange={(e) => setDet({ ...det, city: e.target.value })} className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" /></label>
              <label className="text-[13px] sm:col-span-2"><span className="mb-1 block font-semibold text-token-secondary">Address</span><input value={det.address} onChange={(e) => setDet({ ...det, address: e.target.value })} className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" /></label>
            </div>
            <div className="flex justify-end border-t border-token-subtle px-5 py-3"><button onClick={saveDetails} disabled={actBusy === 'det'} className="rounded-[10px] bg-brand px-4 py-2 text-[13px] font-bold text-brand-fg disabled:opacity-60">{actBusy === 'det' ? 'Saving…' : 'Save details'}</button></div>
          </div>
        </div>

        {reqOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setReqOpen(false)}>
            <div className="w-full max-w-md rounded-[16px] bg-token-surface p-6" onClick={(e) => e.stopPropagation()}>
              <p className="font-display text-[17px] font-bold text-token-primary">Request a job</p>
              <p className="mt-1 text-[13px] text-token-secondary">Tell your trade person what you need and they’ll get back to you.</p>
              <div className="mt-4 space-y-3">
                {data.businesses.length > 1 && (
                  <label className="block text-[13px]"><span className="mb-1 block font-semibold text-token-secondary">Business</span>
                    <select value={req.companyId} onChange={(e) => setReq({ ...req, companyId: e.target.value })} className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px]">{data.businesses.map((b) => <option key={b.companyId} value={b.companyId}>{b.name}</option>)}</select></label>
                )}
                <label className="block text-[13px]"><span className="mb-1 block font-semibold text-token-secondary">What do you need?</span><input value={req.subject} onChange={(e) => setReq({ ...req, subject: e.target.value })} placeholder="e.g. Leaking tap in the bathroom" className="h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] outline-none focus:border-token-strong" /></label>
                <label className="block text-[13px]"><span className="mb-1 block font-semibold text-token-secondary">Details (optional)</span><textarea value={req.details} onChange={(e) => setReq({ ...req, details: e.target.value })} rows={4} className="w-full rounded-[10px] border border-token-subtle px-3 py-2 text-[14px] outline-none focus:border-token-strong" /></label>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setReqOpen(false)} className="rounded-[10px] border border-token-subtle px-4 py-2 text-[13px] font-semibold text-token-secondary">Cancel</button>
                <button onClick={submitRequest} disabled={actBusy === 'req'} className="rounded-[10px] bg-brand px-4 py-2 text-[13px] font-bold text-brand-fg disabled:opacity-60">{actBusy === 'req' ? 'Sending…' : 'Send request'}</button>
              </div>
            </div>
          </div>
        )}

        <p className="mt-6 flex items-center justify-center gap-1.5 text-[12px] text-token-muted"><ShieldCheck className="h-3.5 w-3.5" /> Secured by MySerbisyo</p>
      </div>
    </div>
  )
}
